/**
 * 项目概览弹窗组件
 * 点击项目卡片后弹出，展示项目详细数据
 * 底部按钮固定显示，不随内容滚动
 */
import React, { useState, useEffect, useRef } from 'react';
import { Modal, Spin, message, Form, Input, InputNumber, DatePicker, Select, Drawer, Button, Space } from 'antd';
import dayjs from 'dayjs';
import type { ProjectOverviewData } from '../../services/dashboardService';
import { StatisticsService } from '../../services/statisticsService';
import { getBudgetDetail } from '../../services/budgetService';
import { useResponsiveConfig } from '../../responsive/hooks/useResponsiveConfig';
import api from '../../services/api';
import { CoordinationCostService } from '../../services/coordinationCostService';
import { CoordinationCostType } from '../../types';
import type { CostDetailType } from './CostDetailTypes';
import BudgetComparisonEnhanced from './BudgetComparisonEnhanced';
import ProjectInfo from './ProjectInfo';
import AddRecordModal, { RecordType } from './AddRecordModal';
import ActionButtons from './ActionButtons';
import WorkTimePanel from './DetailRecords/WorkTimePanel';
import EquipmentPanel from './DetailRecords/EquipmentPanel';
import MaterialPanel from './DetailRecords/MaterialPanel';
import SubcontractPanel from './DetailRecords/SubcontractPanel';
import CoordinationPanel from './DetailRecords/CoordinationPanel';

const { TextArea } = Input;
type QuickAddType = 'subcontract' | 'coordination';

export interface ProjectOverviewModalProps {
  visible: boolean;
  project: ProjectOverviewData | null;
  onClose: () => void;
  onEditProject?: (project: ProjectOverviewData) => void;
}

interface CacheData {
  data: any;
  timestamp: number;
}

const CACHE_TTL = 60 * 1000;
const statsCache = new Map<string, CacheData>();

const DETAIL_PANEL_CONFIG: Record<CostDetailType, { title: string; fullPageText: string }> = {
  worktime: { title: '人工成本明细', fullPageText: '打开完整记录页 · 工时' },
  equipment: { title: '机械成本明细', fullPageText: '打开完整记录页 · 设备' },
  material: { title: '材料成本明细', fullPageText: '打开完整记录页 · 材料' },
  subcontract: { title: '分包成本明细', fullPageText: '打开完整记录页 · 分包' },
  coordination: { title: '协调成本明细', fullPageText: '打开完整记录页 · 协调' },
};


const clearProjectStatsCache = (projectId?: string) => {
  if (projectId) {
    statsCache.delete(projectId);
  } else {
    statsCache.clear();
  }
};

const ProjectOverviewModal: React.FC<ProjectOverviewModalProps> = ({
  visible,
  project,
  onClose,
  onEditProject
}) => {
  const { isMobile, isTablet } = useResponsiveConfig();
  const [loading, setLoading] = useState(false);
  const [projectStats, setProjectStats] = useState<any>(null);
  const [budgetDetail, setBudgetDetail] = useState<any>(null);
  const [addRecordVisible, setAddRecordVisible] = useState(false);
  const [recordType, setRecordType] = useState<RecordType | null>(null);
  const [quickAddVisible, setQuickAddVisible] = useState(false);
  const [quickAddType, setQuickAddType] = useState<QuickAddType>('subcontract');
  const [detailDrawerType, setDetailDrawerType] = useState<CostDetailType | null>(null);
  const [quickAddForm] = Form.useForm();
  const loadingRef = useRef(false);

  const fetchProjectStats = async (projectId: string, forceRefresh = false) => {
    if (!forceRefresh) {
      const cached = statsCache.get(projectId);
      if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
        setProjectStats(cached.data);
        return;
      }
    }

    if (loadingRef.current) return;
    loadingRef.current = true;

    setLoading(true);
    try {
      const [response, budgetResponse] = await Promise.all([
        StatisticsService.getProjectComprehensiveStats(projectId),
        getBudgetDetail(projectId),
      ]);
      if (response.success && response.data) {
        setProjectStats(response.data);
        statsCache.set(projectId, {
          data: response.data,
          timestamp: Date.now()
        });
      }
      if (budgetResponse.success && budgetResponse.data) {
        setBudgetDetail(budgetResponse.data);
      }
    } catch (error) {
      console.error('获取项目统计数据失败:', error);
      message.error('获取项目统计数据失败');
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  useEffect(() => {
    if (visible && project?.id) {
      fetchProjectStats(project.id);
    }
    if (!visible) {
      setProjectStats(null);
      setBudgetDetail(null);
      setAddRecordVisible(false);
      setRecordType(null);
      setQuickAddVisible(false);
      setDetailDrawerType(null);
    }
  }, [visible, project?.id, quickAddForm]);

  const getModalWidth = () => {
    if (isMobile) return '100%';
    if (isTablet) return '90%';
    return 900;
  };

  const openAddRecord = (type: RecordType) => {
    setRecordType(type);
    setAddRecordVisible(true);
  };

  const closeAddRecord = () => {
    setAddRecordVisible(false);
    setRecordType(null);
  };

  const openQuickAdd = (type: QuickAddType) => {
    setQuickAddType(type);
    setQuickAddVisible(true);
    quickAddForm.resetFields();
    quickAddForm.setFieldsValue({
      projectId: project?.id,
      costDate: dayjs(),
      status: 'active',
      costType: CoordinationCostType.MISC,
      paidAmount: 0,
    });
  };

  const closeQuickAdd = () => {
    setQuickAddVisible(false);
    quickAddForm.resetFields();
  };

  const handleAddSuccess = () => {
    if (!project?.id) return;
    clearProjectStatsCache(project.id);
    fetchProjectStats(project.id, true);
  };

  const handleQuickAddSubmit = async () => {
    if (!project?.id) return;

    try {
      const values = await quickAddForm.validateFields();

      if (quickAddType === 'subcontract') {
        await api.post('/subcontract-costs', {
          projectId: project.id,
          contractName: values.contractName,
          costDate: values.costDate.format('YYYY-MM-DD'),
          contractAmount: values.contractAmount,
          paidAmount: values.paidAmount || 0,
          status: values.status,
          description: values.description,
        });
        message.success('分包成本记录创建成功');
      } else {
        await CoordinationCostService.createCoordinationCost({
          projectId: project.id,
          costDate: values.costDate.format('YYYY-MM-DD'),
          costType: values.costType,
          amount: values.amount,
          handler: values.handler,
          description: values.description,
        });
        message.success('协调成本记录创建成功');
      }

      closeQuickAdd();
      handleAddSuccess();
    } catch (error) {
      message.error(quickAddType === 'subcontract' ? '分包成本记录创建失败' : '协调成本记录创建失败');
    }
  };

  const handleInlineAdd = (type: CostDetailType) => {
    if (type === 'worktime' || type === 'equipment' || type === 'material') {
      openAddRecord(type);
      return;
    }

    if (type === 'subcontract') {
      openQuickAdd('subcontract');
      return;
    }

    if (type === 'coordination') {
      openQuickAdd('coordination');
    }
  };

  const handleViewDetail = (type: CostDetailType) => {
    setDetailDrawerType(type);
  };

  const handleOpenFullPage = (type: CostDetailType) => {
    if (!project?.id) return;

    const routeMap: Record<CostDetailType, string> = {
      worktime: `/worktime?projectId=${project.id}`,
      equipment: `/equipment?projectId=${project.id}`,
      material: `/material?projectId=${project.id}`,
      subcontract: `/subcontract-cost-management?projectId=${project.id}`,
      coordination: `/coordination-cost-management?projectId=${project.id}`,
    };

    window.location.href = routeMap[type];
  };

  const renderDetailContent = () => {
    if (!project?.id || !detailDrawerType) return null;

    switch (detailDrawerType) {
      case 'worktime':
        return <WorkTimePanel projectId={project.id} active isMobile={isMobile} />;
      case 'equipment':
        return <EquipmentPanel projectId={project.id} active isMobile={isMobile} />;
      case 'material':
        return <MaterialPanel projectId={project.id} active isMobile={isMobile} />;
      case 'subcontract':
        return <SubcontractPanel projectId={project.id} active isMobile={isMobile} />;
      case 'coordination':
        return <CoordinationPanel projectId={project.id} active isMobile={isMobile} />;
      default:
        return null;
    }
  };

  const handleBudgetRefresh = () => {
    if (!project?.id) return;
    clearProjectStatsCache(project.id);
    fetchProjectStats(project.id, true);
  };

  if (!project) return null;

  const mergedProject = {
    ...project,
    budget: budgetDetail?.budget ?? project.budget,
    budgetBreakdown: budgetDetail?.budgetBreakdown,
    revenueTotal: budgetDetail?.revenueTotal,
    revenueBreakdown: budgetDetail?.revenueBreakdown,
    budgetVersion: budgetDetail?.budgetVersion,
    budgetLocked: budgetDetail?.budgetLocked ?? project.budgetLocked,
    budgetLockedAt: budgetDetail?.budgetLockedAt,
    budgetLockedBy: budgetDetail?.budgetLockedBy,
    actualCost: projectStats?.summary?.totalProjectCost || project.actualCost || 0,
  };

  const renderFooter = () => (
    <ActionButtons
      project={mergedProject}
      onEditProject={onEditProject}
      isMobile={isMobile}
    />
  );

  return (
    <>
      <Modal
        title={<span style={{ color: '#10171d' }}>项目概览 · {project.name}</span>}
        open={visible}
        onCancel={onClose}
        footer={renderFooter()}
        width={getModalWidth()}
        style={isMobile ? { top: 0, margin: 0, maxWidth: '100%', paddingBottom: 0 } : { top: 24 }}
        styles={{
          body: {
            maxHeight: isMobile ? 'calc(100vh - 220px)' : 'calc(78vh - 80px)',
            overflow: 'auto',
            padding: isMobile ? '12px' : '24px',
          },
          footer: {
            borderTop: '1px solid rgba(16, 27, 35, 0.08)',
            padding: isMobile ? '0 12px' : '0 24px',
            margin: 0,
            background: 'rgba(232, 237, 240, 0.98)'
          },
          header: {
            background: 'rgba(232, 237, 240, 0.98)',
            borderBottom: '1px solid rgba(16, 27, 35, 0.08)'
          }
        }}
        destroyOnHidden
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <Spin size="large" tip="加载项目数据中..." />
          </div>
        ) : (
          <div>
            <BudgetComparisonEnhanced
              project={mergedProject}
              stats={projectStats}
              isMobile={isMobile}
              onBudgetLockChange={handleBudgetRefresh}
              onEditProject={onEditProject ? () => onEditProject(mergedProject) : undefined}
              onViewDetail={handleViewDetail}
              onAddRecord={handleInlineAdd}
            />
            <ProjectInfo project={mergedProject} isMobile={isMobile} />
          </div>
        )}
      </Modal>

      <Drawer
        title={detailDrawerType ? DETAIL_PANEL_CONFIG[detailDrawerType].title : '成本明细'}
        open={!!detailDrawerType}
        onClose={() => setDetailDrawerType(null)}
        width={isMobile ? '100%' : 700}
        styles={{
          header: { background: 'rgba(232, 237, 240, 0.98)', borderBottom: '1px solid rgba(16, 27, 35, 0.08)' },
          body: { background: 'rgba(232, 237, 240, 0.98)' }
        }}
        extra={detailDrawerType ? (
          <Space>
            <Button size="small" onClick={() => handleOpenFullPage(detailDrawerType)}>
              {DETAIL_PANEL_CONFIG[detailDrawerType].fullPageText}
            </Button>
          </Space>
        ) : null}
        destroyOnHidden
      >
        {renderDetailContent()}
      </Drawer>

      <AddRecordModal
        visible={addRecordVisible}
        recordType={recordType}
        projectId={project.id}
        projectName={project.name}
        onClose={closeAddRecord}
        onSuccess={handleAddSuccess}
        isMobile={isMobile}
      />

      <Modal
        title={quickAddType === 'subcontract' ? '新增分包成本' : '新增协调成本'}
        open={quickAddVisible}
        onCancel={closeQuickAdd}
        onOk={handleQuickAddSubmit}
        okText="保存"
        destroyOnHidden
        styles={{
          header: { background: 'rgba(232, 237, 240, 0.98)', borderBottom: '1px solid rgba(16, 27, 35, 0.08)' },
          body: { background: 'rgba(232, 237, 240, 0.98)' },
          footer: { background: 'rgba(232, 237, 240, 0.98)', borderTop: '1px solid rgba(16, 27, 35, 0.08)' }
        }}
      >
        <Form form={quickAddForm} layout="vertical">
          {quickAddType === 'subcontract' ? (
            <>
              <Form.Item name="contractName" label="合同名称" rules={[{ required: true, message: '请输入合同名称' }]}>
                <Input placeholder="请输入合同名称" />
              </Form.Item>
              <Form.Item name="costDate" label="日期" rules={[{ required: true, message: '请选择日期' }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="contractAmount" label="合同金额" rules={[{ required: true, message: '请输入合同金额' }]}>
                <InputNumber style={{ width: '100%' }} min={0} precision={2} placeholder="请输入合同金额" />
              </Form.Item>
              <Form.Item name="paidAmount" label="已付金额">
                <InputNumber style={{ width: '100%' }} min={0} precision={2} placeholder="请输入已付金额" />
              </Form.Item>
              <Form.Item name="status" label="状态" rules={[{ required: true, message: '请选择状态' }]}>
                <Select options={[{ label: '进行中', value: 'active' }, { label: '已完成', value: 'completed' }, { label: '已取消', value: 'cancelled' }]} />
              </Form.Item>
              <Form.Item name="description" label="备注">
                <TextArea rows={3} placeholder="备注（可选）" />
              </Form.Item>
            </>
          ) : (
            <>
              <Form.Item name="costDate" label="日期" rules={[{ required: true, message: '请选择日期' }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="costType" label="费用类型" rules={[{ required: true, message: '请选择费用类型' }]}>
                <Select options={CoordinationCostService.getCostTypeOptions()} />
              </Form.Item>
              <Form.Item name="amount" label="金额" rules={[{ required: true, message: '请输入金额' }]}>
                <InputNumber style={{ width: '100%' }} min={0} precision={2} placeholder="请输入金额" />
              </Form.Item>
              <Form.Item name="handler" label="经办人">
                <Input placeholder="请输入经办人" />
              </Form.Item>
              <Form.Item name="description" label="备注">
                <TextArea rows={3} placeholder="备注（可选）" />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>
    </>
  );
};

export default ProjectOverviewModal;
